import OverlayTrigger from '@rio-cloud/rio-uikit/components/overlay/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/components/tooltip/Tooltip';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';

import {
  Bubble,
  BubbleWrapper,
  DeleteIcon,
  EditIcon,
  HoverArea,
} from '@/components/organism/SpeechBubble/SpeechBubble.styles';
import { deleteDialogText, tooltipIconsText } from '@/components/organism/SpeechBubble/SpeechBubble.text';
import { THandleSendParams } from '@/components/pages/ChatPage/types';
import { useAppSelector } from '@/configuration/setup/hooks';
import { selectMessageById } from '@/store/chat/chat.slice';
import { forceModalClose } from '@/utils/modalSub';

import BubbleContent from '../../molecule/BubbleContent/BubbleContent';
import { MessageActionBar } from '../MessageActionBar/MessageActionBar';

export type SpeechBubbleProps = {
  isLoading?: boolean;
  messageId: string;
  handleSend: (params: THandleSendParams) => void;
  handleDelete: (messageId: string) => void;
};

const SpeechBubble = ({ messageId, isLoading, handleSend, handleDelete }: SpeechBubbleProps) => {
  const intl = useIntl();
  const { role, isOnboardingQuestion } = useAppSelector(selectMessageById(messageId));
  const isMobileOrTablet = useMediaQuery({ maxWidth: '64rem' });
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleEditClick = () => setIsEditing(true);
  const handleDeleteClick = (submit: boolean) => {
    setShowDeleteDialog(false);
    forceModalClose();
    if (submit) {
      handleDelete(messageId);
    }
  };

  return (
    <BubbleWrapper role={role}>
      {role === 'ai' ? (
        <div className="text-color-darker text-size-14 text-medium margin-bottom-5 gap-5 align-items-center">
          <span className="rioglyph rioglyph-robot text-size-16 margin-right-5" aria-hidden="true" />
          <span>Fleet Assistant</span>
        </div>
      ) : null}

      <HoverArea $ismobileortablet={isMobileOrTablet} role={role} $isAi={role === 'ai'}>
        {role === 'human' && !isEditing && (
          <>
            <OverlayTrigger
              key="edit-tooltip"
              placement="bottom-start"
              overlay={
                <Tooltip id="tooltip-edit" allowOnTouch>
                  <span>{intl.formatMessage({ id: tooltipIconsText.edit })}</span>
                </Tooltip>
              }
            >
              <EditIcon
                onClick={!isLoading ? handleEditClick : undefined}
                data-testid="icon-edit"
                className="rioglyph rioglyph-pencil"
                disabled={isLoading}
              />
            </OverlayTrigger>

            <OverlayTrigger
              key="delete-tooltip"
              placement="bottom-start"
              overlay={
                <Tooltip id="tooltip-delete" allowOnTouch>
                  <span>{intl.formatMessage({ id: tooltipIconsText.delete })}</span>
                </Tooltip>
              }
            >
              <DeleteIcon
                onClick={!isLoading ? () => setShowDeleteDialog(true) : undefined}
                data-testid="icon-trash"
                className="rioglyph rioglyph-trash"
                disabled={isLoading}
              />
            </OverlayTrigger>
          </>
        )}
        <Bubble role={role} $isediting={isEditing} data-testid={`speech_bubble-${role}`}>
          {messageId && isLoading !== undefined ? (
            <BubbleContent
              messageId={messageId}
              isLoading={isLoading}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleSend={handleSend}
            />
          ) : null}
          {role === 'ai' && !isOnboardingQuestion ? (
            <MessageActionBar messageId={messageId} handleSend={handleSend} />
          ) : null}
        </Bubble>
      </HoverArea>

      <ConfirmationDialog
        show={showDeleteDialog}
        title={intl.formatMessage({ id: deleteDialogText.title })}
        content={intl.formatMessage({ id: deleteDialogText.content })}
        cancelButtonText={intl.formatMessage({ id: deleteDialogText.cancel })}
        onClickCancel={() => handleDeleteClick(false)}
        onClickConfirm={() => handleDeleteClick(true)}
        confirmButtonText={
          <>
            <span className="rioglyph rioglyph-ok margin-right-5" />
            <span>{intl.formatMessage({ id: deleteDialogText.confirm })}</span>
          </>
        }
      />
    </BubbleWrapper>
  );
};

export default SpeechBubble;
